import {Trans} from '@lingui/macro';
import React, {FC, useContext, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PushContext} from '../../../../../srcCommon/push/pushContext';
import {buildLensPath} from '../../../../helpers/modelHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {TeammateLicenseTypesEnum} from '../../../../models/api/teammateModel';
import {InboxesFiltersEnum} from '../../../../models/url/inboxesUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspaceDragSourceTypesEnum} from '../dnd/workspaceDragDropTypes';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {Workspace} from '../workspace';
import {WorkspaceHeader} from '../workspaceHeader';
import {LensesWorkspacesCreateDialogContainer} from './lensesWorkspaceCreateDialogContainer';
import {LensesWorkspaceItemContainer} from './lensesWorkspaceItemContainer';

/*
 * Props.
 */

export interface LensesWorkspaceProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: Handler;
  /** Current user license (full or light). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether this user is a global admin. */
  isUserGlobalAdmin: boolean;
  /** The list of lenses to display in this workspace. */
  lenses: ReadonlyArray<LensModel>;
  shouldAlphabetizeWorkspace: boolean;
  defaultStatusFilter: InboxesFiltersEnum.OPEN | InboxesFiltersEnum.UNASSIGNED;
}

/*
 * Styles.
 */

const StyledCreateCtaDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.palette.blue.shade40};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

/** Displays the workspace of private and shared lenses. */
export const LensesWorkspace: FC<LensesWorkspaceProps> = (props) => {
  const {isConnected} = useContext(PushContext);
  const {isExpanded, userLicenseType} = props;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Hide the workspace completely for light teammates.
  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  const onCreateClick = () => setIsCreateDialogOpen(true);

  const isOffline = !isConnected;

  return (
    <>
      <Workspace isExpanded={isExpanded}>
        {/* Header. */}
        <WorkspaceHeader
          interactionId="lenses"
          isExpanded={isExpanded}
          onToggleExpandClick={onToggleExpandClick}
          isOffline={isOffline}
        >
          <Trans id="lenses-workspace-heading">Saved views</Trans>
        </WorkspaceHeader>

        {/* Workspace content. */}
        {maybeRenderExpandedContent(props, isOffline, onCreateClick)}
      </Workspace>
      {isCreateDialogOpen && (
        <LensesWorkspacesCreateDialogContainer onRequestClose={() => setIsCreateDialogOpen(false)} />
      )}
    </>
  );
};

function trackLensClick(lens: LensModel) {
  trackInterfaceEvent(InterfaceEventsEnum.CLICK_LENS_P1, {lens_id: lens.id});
}

function maybeRenderExpandedContent(props: LensesWorkspaceProps, isOffline: boolean, onCreateClick: Handler) {
  const {panelId, isExpanded, shouldAlphabetizeWorkspace, defaultStatusFilter} = props;

  if (!isExpanded) {
    return null;
  }

  const lastIndex = props.lenses.length - 1;

  return (
    <>
      {/* Lenses. */}
      {props.lenses.map((lens, index) => (
        <LensesWorkspaceItemContainer
          key={lens.id}
          panelId={panelId}
          lens={lens}
          to={buildLensPath(lens.id, defaultStatusFilter, isTeammateNamespace(lens.namespace))}
          onClick={() => trackLensClick(lens)}
          // TODO-LENS-COUNTERS: add lens counters.
          // counterKey={buildTeammateAssignedCounterKey(t.id)}
          isDisabled={isOffline}
          // TODO-LENS: determine if its easier to support reordering for Fronteer release than remove this.
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.LENS,
            id: lens.id,
            isLast: index === lastIndex,
          }}
          shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
        />
      ))}

      {renderCreateLensButton(onCreateClick)}
    </>
  );
}

/*
 * Helpers.
 */

function renderCreateLensButton(onClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <WorkspaceLineDropTarget
          indentLevel={0}
          onClick={onClick}
          shouldAlphabetizeWorkspace={false}
          workspaceDragItem={undefined}
          // TODO-LENS: Will need this for edit mode version.
          // isSelected={shouldShowPrivateTagsDropdown}
        >
          {<NavigationLineStandardIcon iconName="plusCircleSmall" color={theme.palette.blue.shade40} />}
          <NavigationLineLabel>
            <StyledCreateCtaDiv>
              <Trans id="lenses-workspace-create-lens">Create view</Trans>
            </StyledCreateCtaDiv>
          </NavigationLineLabel>
        </WorkspaceLineDropTarget>
      )}
    </UserThemeConsumer>
  );
}
